import type { AuthUser } from '@e-learning/core';
import {
  AuthProvider,
  getLoginRedirectPath,
  useAuthContext,
  useAuthGate,
} from '@e-learning/core';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useNavigate,
} from 'react-router-dom';
import { authStorage } from './auth';
import { AlunoShell } from './components/AlunoShell';
import { AppShell } from './components/AppShell';
import { CoursesPage, CreateCoursePage } from './pages/Admin/Courses';
import { Dashboard } from './pages/Admin/Dashboard';
import { ForgotPasswordPage } from './pages/Admin/ForgotPassword';
import { GamificationPage } from './pages/Admin/Gamification';
import { GroupsPage } from './pages/Admin/Groups';
import { LeaderboardPage } from './pages/Admin/Leaderboard';
import { LoginPage } from './pages/Admin/Login';
import { CreateQuestionBankPage, QuestionBankPage } from './pages/Admin/QuestionBank';
import { UsersPage } from './pages/Admin/Users';
import { DashboardAluno, MeusCursosPage } from './pages/Student';

const AUTH_KEY = 'e-learning-b2b-auth';
const AUTH_DATA_KEY = 'e-learning-b2b-auth-data';

/** Nome exibido no header quando AuthUser não tem campo name (derivado do email). */
function getDisplayNameFromEmail(email: string | undefined): string {
  if (!email) return 'Usuário';
  const part = email.split('@')[0]?.trim();
  return part
    ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
    : 'Usuário';
}

function AppRoutes() {
  const navigate = useNavigate();
  const { user, setUser, hasBothProfiles } = useAuthContext();
  const authGateResult = useAuthGate({
    authKey: AUTH_KEY,
    tokenPrefix: import.meta.env.VITE_AUTH_TOKEN_PREFIX ?? 'jwt-teste-b2b',
    standalonePort: '3001',
    basename: '/',
    authDataKey: AUTH_DATA_KEY,
  });

  const isHostedEnvironment =
    typeof window !== 'undefined' &&
    !['localhost', '127.0.0.1'].includes(window.location.hostname);
  const isProduction = window.location.hostname.includes('azurestaticapps.net');
  const isStandalone = isProduction || isHostedEnvironment || authGateResult.isStandalone;
  const { auth, handleLogin, handleLogout } = authGateResult;

  const onLoginSuccess = (user: AuthUser) => {
    setUser(user);
    handleLogin();
    navigate(getLoginRedirectPath(user));
  };

  const userName = user ? getDisplayNameFromEmail(user.email) : 'Usuário';
  const userEmail = user?.email ?? '';

  const adminLayoutElement = (
    <AppShell
      userName={userName}
      userEmail={userEmail}
      onLogout={handleLogout}
      viewAs={hasBothProfiles ? undefined : false}
    >
      <Outlet />
    </AppShell>
  );

  const alunoLayoutElement = (
    <AlunoShell
      userName={userName}
      userEmail={userEmail}
      onLogout={handleLogout}
      viewAs={hasBothProfiles ? undefined : false}
    >
      <Outlet />
    </AlunoShell>
  );

  const authRoutes = (
    <>
      <Route path="/" element={<Navigate to="/admin" replace />} />
      <Route path="/admin/question-bank/new" element={<CreateQuestionBankPage />} />
      <Route path="/admin/courses/new" element={<CreateCoursePage />} />
      <Route
        path="/admin/courses/:courseId/edit"
        element={<CreateCoursePage />}
      />
      <Route path="/student/meus-cursos" element={<MeusCursosPage />} />
      <Route path="/admin" element={adminLayoutElement}>
        <Route index element={<Dashboard />} />
        <Route path="groups" element={<GroupsPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="courses" element={<CoursesPage />} />
        <Route path="question-bank" element={<QuestionBankPage />} />
        <Route path="gamification" element={<GamificationPage />} />
        <Route path="leaderboard" element={<LeaderboardPage />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Route>
      <Route path="/student" element={alunoLayoutElement}>
        <Route index element={<DashboardAluno />} />
        <Route path="leaderboard" element={<LeaderboardPage />} />
        <Route path="*" element={<Navigate to="/student" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/admin" replace />} />
    </>
  );

  return (
    <Routes>
      {isStandalone ? (
        <>
          <Route
            path="/forgot-password"
            element={
              auth ? <Navigate to="/admin" replace /> : <ForgotPasswordPage />
            }
          />
          {auth ? (
            authRoutes
          ) : (
            <Route path="*" element={<LoginPage onLogin={onLoginSuccess} />} />
          )}
        </>
      ) : (
        authRoutes
      )}
    </Routes>
  );
}

function AppWithRouter() {
  const isHostedEnvironment =
    typeof window !== 'undefined' &&
    !['localhost', '127.0.0.1'].includes(window.location.hostname);
  const isProduction =
    typeof window !== 'undefined' &&
    window.location.hostname.includes('azurestaticapps.net');
  const isStandalone =
    isProduction ||
    isHostedEnvironment ||
    (typeof window !== 'undefined' && window.location.port === '3001');
  const basename = isStandalone ? '' : '/';

  return (
    <BrowserRouter basename={basename}>
      <AppRoutes />
    </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider authStorage={authStorage}>
      <AppWithRouter />
    </AuthProvider>
  );
}

export default App;
